import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TableRow,
  TableCell,
  Box,
} from "@mui/material";
import { ExpandMore, LocalOffer } from "@mui/icons-material";
import {
  createPersonnelTag,
  deletePersonnelTag,
  getPersonnelTags,
  updatePersonnelTag,
} from "../../reducers/PersonnelTagsReducer";
import PATAddButton from "../Buttons/PATAddButton";
import CustomTable from "../CustomTable";
import PATEditButton from "../Buttons/PATEditButton";
import PATDeleteButton from "../Buttons/PATDeleteButton";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import TagDialog from "./TagDialog";
import { getAccessPermission } from "../../reducers/PermissionsReducer";
import { PerTagType } from "../../utils/enums";

const tableHeaders = ["Description", "Value", "Actions"];

const TagsTable = ({
  id,
  tagTypeOptions,
  personnelTags,
  theme,
  allExpanded = false,
  perTagType,
  readOnly
}) => {
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.user);
  const [openTagDialog, setOpenTagDialog] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);
  const [expanded, setExpanded] = useState(allExpanded);
  const [isLoadData, setIsLoadData] = useState(false);

  const getTagTabPermissions = () => {
    if (perTagType === PerTagType.Applicant) {
      return getAccessPermission("applicants", "Tags Tab", "any");
    }
    if (perTagType === PerTagType.Employee) {
      return getAccessPermission("employees", "Tags Tab", "any");
    }
    if (perTagType === PerTagType.Course) {
      return getAccessPermission("training", "Course Tags", "any");
    }
    if (perTagType === PerTagType.Participant) {
      return getAccessPermission("training", "Participant Tags", "any");
    }
    if (perTagType === PerTagType.Certification) {
      return getAccessPermission("training", "Certificate Tags", "any");
    }
    return null;
  };

  const tagTabPermission = getTagTabPermissions();
  const canAdd = tagTabPermission?.Create ?? true;
  const canEdit = tagTabPermission?.Edit ?? true;
  const canDelete = tagTabPermission?.Delete ?? true;

  useEffect(() => {
    setExpanded(allExpanded);
  }, [allExpanded]);

  useEffect(() => {
    if (id && !isLoadData && (!personnelTags || personnelTags?.length === 0) && expanded) {
      dispatch(getPersonnelTags(id, perTagType));
      setIsLoadData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, expanded, perTagType, dispatch]);

  const handleOpenAddTag = (e) => {
    e.stopPropagation();
    setEditingTag(null);
    setOpenTagDialog(true);
  };

  const handleOpenEditTag = (e, tag) => {
    e.stopPropagation();
    setEditingTag(tag);
    setOpenTagDialog(true);
  };

  const handleSaveTag = (data) => {
    if (editingTag) {
      dispatch(
        updatePersonnelTag(editingTag.perTagID, { ...data, perEntityID: id, perEntityName: perTagType })
      );
    } else {
      dispatch(
        createPersonnelTag({
          ...data,
          perEntityID: id,
          perEntityName: perTagType,
        })
      );
    }
  };

  const handleOpenDeleteDialog = (e, tagId) => {
    e.stopPropagation();
    setTagToDelete(tagId);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (tagToDelete) {
      dispatch(
        deletePersonnelTag(tagToDelete, userData?.user?.ptsUserID, id, perTagType)
      ).then(() => dispatch(getPersonnelTags(id, perTagType)));
    }
    setOpenDeleteDialog(false);
    setTagToDelete(null);
  };

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded((prev) => !prev)}
        sx={{ mb: 1 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: theme.accordion.backgroundColor,
            color: theme.accordion.textColor,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Box display="flex" alignItems="center">
              <LocalOffer sx={{ mr: 1 }} />
              <span>Tags</span>
            </Box>
            <Box sx={{ mr: 2 }}>
              {!readOnly && canAdd && (
                <PATAddButton
                  tooltipText="Attach Tag"
                  onClick={handleOpenAddTag}
                />
              )}
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {personnelTags && personnelTags.length > 0 ? (
            <CustomTable
              columns={tableHeaders}
              data={personnelTags}
              renderRow={(tag, index) => (
                <TableRow key={index}>
                  <TableCell>{tag.tagInfo?.description}</TableCell>
                  <TableCell>{tag.value}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      {!readOnly && canEdit && (
                        <PATEditButton
                          tooltipText="Edit Personnel Tag"
                          onClick={(e) => handleOpenEditTag(e, tag)}
                          size="20px"
                        />
                      )}
                      {!readOnly && canDelete && (
                        <PATDeleteButton
                          tooltipText="Delete Personnel Tag"
                          onClick={(e) =>
                            handleOpenDeleteDialog(e, tag.perTagID)
                          }
                          size="20px"
                        />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            />
          ) : (
            <span>No Tags Available</span>
          )}
        </AccordionDetails>
      </Accordion>
      <TagDialog
        open={openTagDialog}
        onClose={() => setOpenTagDialog(false)}
        onSave={handleSaveTag}
        initialData={editingTag}
        tagTypeOptions={tagTypeOptions}
      />
      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
          setTagToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Personnel Tag"
        message="Are you sure you want to delete this personnel tag?"
      />
    </>
  );
};

export default TagsTable;
